import { useEffect, useRef } from "react";
import Plotly from "plotly.js-dist-min";
import { RACE_COLORS, FONT_FAMILY, IS_TOUCH, BAR_HEIGHT, BAR_PADDING_DISPARITY } from "../constants.js";

function disparityColor(ratio) {
  if (ratio == null) return "#aaaaaa";
  if (Math.round(ratio * 100) === 100) return "#333333";
  if (ratio > 1) return RACE_COLORS.BLACK;
  return RACE_COLORS.WHITE;
}

function disparityLabel(b_pct, w_pct) {
  if (b_pct == null || w_pct == null || w_pct === 0 || b_pct === 0) return "N/A";
  if (b_pct >= w_pct) return `${(b_pct / w_pct).toFixed(2)}x`;
  return `${(w_pct / b_pct).toFixed(2)}x`;
}

export default function DisparityDot({ data, title, subtitle, height, sortOrder }) {
  const ref = useRef();

  useEffect(() => {
    if (!data || !data.length) return;

    // Only categories with a computable ratio get a dot
    const valid = data.filter(d => d.ratio != null);
    if (!valid.length) return;

    // Full y-axis category list = sortOrder from the paired bar chart.
    // sortOrder already excludes NOT RECORDED when the checkbox is off (handled by the generation script),
    // so we use it directly — no further filtering here.
    const allCategories = sortOrder && sortOrder.length
      ? sortOrder
      : valid.map(d => d.category_value);

    // Height matches the bar chart plot area exactly.
    // Bar chart uses BAR_PADDING=170 with b:60; disparity uses b:130 (70px more),
    // so BAR_PADDING_DISPARITY=240 keeps the plot area height identical.
    const n = allCategories.length;
    const h = height || n * BAR_HEIGHT * 2 + BAR_PADDING_DISPARITY;

    // Keep the "More likely for" annotations a consistent ~45px below the x-axis
    // regardless of chart height. Paper coords: y = -(pixels / plot_area_height).
    // plot area height = total height - top margin (90) - bottom margin (130).
    const plotAreaH = n * BAR_HEIGHT * 2 + BAR_PADDING_DISPARITY - 90 - 130;
    const annotY = -(45 / plotAreaH);

    const xVals = valid.map(d => Math.log2(d.ratio));
    const yVals = valid.map(d => d.category_value);
    const colors = valid.map(d => disparityColor(d.ratio));
    const labels = valid.map(d => disparityLabel(d.b_pct, d.w_pct));

    // Lollipop stems — only for valid points
    const shapes = valid.map((d, i) => ({
      type: "line",
      x0: 0,
      x1: Math.log2(d.ratio),
      y0: d.category_value,
      y1: d.category_value,
      xref: "x",
      yref: "y",
      line: { color: colors[i], width: 2 },
    }));

    // Vertical dashed reference line at x=0
    shapes.push({
      type: "line",
      x0: 0, x1: 0,
      y0: 0, y1: 1,
      xref: "x", yref: "paper",
      line: { color: "#333333", width: 1.5, dash: "dash" },
    });

    const tickvals = [-2, -1, 0, 1, 2];
    const ticktext = ["", "", "1x", "", ""];

    const allX = xVals.filter(v => isFinite(v));
    const absMax = Math.max(Math.abs(Math.min(...allX, -0.5)), Math.abs(Math.max(...allX, 0.5))) + 2.0;
    const xMin = -absMax - 3.5;  // extra left buffer so text labels on left-side dots never overflow into the y-axis margin
    const xMax = absMax;

    const fullTitle = `<b>${title}</b>`;

    // Invisible placeholder trace that includes every category in allCategories.
    // Plotly scatter drops y-axis rows with no data points even when categoryarray is set,
    // so this forces every row to render (empty rows stay blank, no hover/tooltip).
    const placeholderTrace = {
      type: "scatter",
      mode: "markers",
      x: allCategories.map(() => null),
      y: allCategories,
      marker: { size: 0, color: "rgba(0,0,0,0)" },
      hoverinfo: "skip",
      showlegend: false,
    };

    Plotly.react(ref.current, [placeholderTrace, {
      type: "scatter",
      mode: "markers+text",
      x: xVals,
      y: yVals,
      marker: { size: 12, color: colors },
      text: labels,
      textposition: xVals.map(x => x >= 0 ? "middle right" : "middle left"),
      textfont: { size: 13, color: colors, family: FONT_FAMILY },
      cliponaxis: false,
      hovertemplate: valid.map(d =>
        `<b>${d.category_value}</b><br>Black: ${d.b_pct}%<br>White: ${d.w_pct}%<br>Ratio: ${disparityLabel(d.b_pct, d.w_pct)}<extra></extra>`
      ),
    }], {
      title: { text: fullTitle, font: { size: 18, color: "#333333", family: FONT_FAMILY }, x: 0, xanchor: "left" },
      height: h,
      margin: { l: 20, r: 110, t: 60, b: 130 },
      shapes,
      xaxis: {
        title: "",
        tickvals,
        ticktext,
        tickfont: { size: 12, family: FONT_FAMILY },
        tickangle: 0,
        range: [xMin, xMax],
        zeroline: false,
        automargin: true,
      },
      yaxis: {
        tickfont: { size: 14, family: FONT_FAMILY },
        automargin: true,
        // Lock y-axis to the same order and set of categories as the paired bar chart
        categoryorder: "array",
        categoryarray: allCategories,
      },
      annotations: [
        {
          x: -0.15,
          y: annotY,
          xref: "x",
          yref: "paper",
          text: "<b>← More likely for<br>White residents</b>",
          xanchor: "right",
          yanchor: "top",
          showarrow: false,
          font: { size: 12, color: RACE_COLORS.WHITE, family: FONT_FAMILY },
        },
        {
          x: 0.15,
          y: annotY,
          xref: "x",
          yref: "paper",
          text: "<b>More likely for<br>Black residents →</b>",
          xanchor: "left",
          yanchor: "top",
          showarrow: false,
          font: { size: 12, color: RACE_COLORS.BLACK, family: FONT_FAMILY },
        },
      ],
      font: { family: FONT_FAMILY },
      paper_bgcolor: "white",
      plot_bgcolor: "white",
      showlegend: false,
    }, { responsive: true, displayModeBar: false, staticPlot: IS_TOUCH });
  }, [data, title, subtitle, height, sortOrder]);

  if (!data || !data.length || !data.some(d => d.ratio != null)) return null;
  return (
    <div>
      {subtitle && <p style={{ fontFamily: FONT_FAMILY, fontSize: 14, fontStyle: "italic", color: "#555", margin: "4px 0 0" }}>{subtitle}</p>}
      <div className="chart-scroll-x">
        <div ref={ref} style={{ minWidth: 520 }} />
      </div>
    </div>
  );
}
